/**
 * §11.11 — display number formatting. Integer only; > 999 abbreviates (`1.2k`).
 * Tabular figures are a CSS concern (`font-variant-numeric: tabular-nums`); this
 * module owns the digits.
 *
 * Every function here is pure and allocation-bounded: callers in per-frame paths
 * must guard with a changed-value check before calling (see `hud.ts`).
 */

const ABBREV_UNITS = ['', 'k', 'M', 'B'] as const;

/**
 * §11.11 integer display. Values below 1000 print exactly; above that they
 * abbreviate to one decimal place, truncated (never rounded up past the unit).
 */
export function formatInt(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const neg = value < 0;
  let v = Math.floor(Math.abs(value));
  if (v < 1000) return neg ? '-' + v : String(v);
  let unit = 0;
  while (v >= 1000 && unit < ABBREV_UNITS.length - 1) {
    v = v / 1000;
    unit++;
  }
  // Truncate to 1dp so 1999 → "1.9k", never "2.0k".
  const truncated = Math.floor(v * 10) / 10;
  const body = truncated >= 100 ? String(Math.floor(truncated)) : truncated.toFixed(1);
  return (neg ? '-' : '') + body + ABBREV_UNITS[unit];
}

/** `MM:SS` run clock (§13.1.4 top-center). Clamps negatives to 00:00. */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return (m < 10 ? '0' + m : String(m)) + ':' + (s < 10 ? '0' + s : String(s));
}

/** One-decimal seconds for cooldown readouts ("0.4 s from ready", §13.1.9). */
export function formatSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  return seconds.toFixed(1);
}

/** Whole-percent display for sliders and multipliers. */
export function formatPercent(fraction01: number): string {
  if (!Number.isFinite(fraction01)) fraction01 = 0;
  return Math.round(fraction01 * 100) + '%';
}

/** `72/100` HP readout. */
export function formatRatio(current: number, max: number): string {
  return formatInt(current) + '/' + formatInt(max);
}

/** Signed delta for level-up copy ("+12", "-0.2"). */
export function formatSigned(value: number, decimals = 0): string {
  const v = decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));
  return value >= 0 ? '+' + v : v;
}

/** §13.1.10 seed display: canonical decimal 32-bit. */
export function formatSeed(seed: number): string {
  return String(seed >>> 0);
}

/** 8-digit lowercase hex, the form the share string carries (§13.7 OQ-13.2). */
export function seedToHex(seed: number): string {
  return (seed >>> 0).toString(16).padStart(8, '0');
}

export interface ShareString {
  seed: number;
  difficulty_id: string;
  content_version: string;
  /** AMD-54. Absent in a v1 string, where all three seeds were the same number. */
  captain_seed?: number;
  hull_seed?: number;
}

/**
 * §13.1.10 share string `LA1:<seed>:<difficulty-id>:<content-version>`, plus AMD-54's
 * optional seed annex `#<captain-seed>#<hull-seed>`, written only when a reroll moved either
 * off the run seed.
 *
 * The annex uses `#` rather than a fourth colon because the content version is defined as
 * *everything* after the third colon, colons included (`tests/ui-format.test.ts` pins
 * `0.1.0:build7`). A colon-delimited tail would be ambiguous against that rule; `#` cannot
 * occur in the head, so both readings stay exact. `parseSeed` in `core/rng.ts` reads the
 * first hex group only, so the whole string still pastes into the seed field (§13.7
 * OQ-13.2).
 */
export function buildShareString(
  seed: number,
  difficultyId: string,
  contentVersion: string,
  captainSeed?: number,
  hullSeed?: number,
): string {
  const base = 'LA1:' + seedToHex(seed) + ':' + difficultyId + ':' + contentVersion;
  const run = seed >>> 0;
  const cap = captainSeed === undefined ? run : captainSeed >>> 0;
  const hull = hullSeed === undefined ? run : hullSeed >>> 0;
  if (cap === run && hull === run) return base;
  return base + '#' + seedToHex(cap) + '#' + seedToHex(hull);
}

/** Inverse of `buildShareString`. Returns null for anything that is not a v1 share string. */
export function parseShareString(input: string): ShareString | null {
  const raw = input.trim();
  if (!raw.startsWith('LA1:')) return null;
  // AMD-54 annex first, so the head parses exactly as it did before it existed.
  const hash = raw.indexOf('#');
  const s = hash < 0 ? raw : raw.slice(0, hash);
  const annex = hash < 0 ? [] : raw.slice(hash + 1).split('#');
  const annexSeed = (i: number): number | undefined =>
    annex[i] !== undefined && /^[0-9a-fA-F]{1,8}$/.test(annex[i]) ? parseInt(annex[i], 16) >>> 0 : undefined;
  const rest = s.slice(4);
  const first = rest.indexOf(':');
  if (first <= 0) return null;
  const hex = rest.slice(0, first);
  if (!/^[0-9a-fA-F]{1,8}$/.test(hex)) return null;
  const second = rest.indexOf(':', first + 1);
  if (second < 0) return null;
  const difficulty = rest.slice(first + 1, second);
  const version = rest.slice(second + 1);
  if (difficulty === '' || version === '') return null;
  return {
    seed: parseInt(hex, 16) >>> 0,
    difficulty_id: difficulty,
    content_version: version,
    captain_seed: annexSeed(0),
    hull_seed: annexSeed(1),
  };
}

/** Human label from a content id: `weapon.spear-jelly-lance` → `Spear Jelly Lance`. */
export function humanizeId(id: string): string {
  const dot = id.indexOf('.');
  const tail = dot >= 0 ? id.slice(dot + 1) : id;
  let out = '';
  let capitalize = true;
  for (let i = 0; i < tail.length; i++) {
    const c = tail[i];
    if (c === '-' || c === '_') {
      out += ' ';
      capitalize = true;
    } else if (capitalize) {
      out += c.toUpperCase();
      capitalize = false;
    } else {
      out += c;
    }
  }
  return out;
}
